import { app } from '../env'
import { TrackingEvent } from '../model/monitoring'
import { authLogout, closeCheckout, persistor, useAppDispatch, useAuthentication, useCheckoutState, useRedirectUrl } from '../state'
import { useMerchantCallback } from './merchant'
import { getCurrentUserContextFromClient, useLDClient } from '@afterpaytouch/feature'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useTransactionMessage } from './transactionMessage'
import { useMonitoringWithEnduringEventProperties } from '../integrations/monitoring'
import { stopDatadogSession } from '@afterpaytouch/integrations/datadog'
import { useRef } from 'react'

export const useCloseCheckout: () => (sendEndTransactionMessage?: boolean) => Promise<void> = () => {
  const { initial, checkout, confirmed, referrer } = useCheckoutState()
  const { endTransaction } = useMerchantCallback()
  const { authenticated, persistent } = useAuthentication()
  const dispatch = useAppDispatch()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const redirectUrl = useRedirectUrl()
  const LDClient = useLDClient()
  const closeCount = useRef(1)
  const endTransactionMessage = useTransactionMessage()

  const reportError = (message: string, error): void => {
    logEvent(TrackingEvent.CHECKOUT_FATAL_ERROR, {
      error: message,
      merchantName: checkout?.merchant.name,
      merchantId: checkout?.merchant.id,
    })

    const context = {
      'Merchant Data': { ...checkout?.merchant },
      'Order confirmed?': { confirmed: typeof confirmed !== 'undefined', ...confirmed },
      'Error details': { ...error },
    }

    capture.error(error, context)
  }

  const clearSession = async (): Promise<void> => {
    dispatch(closeCheckout())
    if (persistor === null) {
      return
    }
    await persistor.flush()
  }

  const getCallbackUrl = (): string => {
    if (redirectUrl.length === 0) {
      reportError('No baseCallbackUrl to return to', undefined)
      return referrer ?? app.BROCHURE_BASE_URL
    }
    return redirectUrl + (initial?.relativeCallbackUrl ?? '')
  }

  const trackCloseCount = (): void => {
    logEvent(TrackingEvent.CLOSE_CALLED, {
      closeCount: closeCount.current,
    })
    closeCount.current += 1
  }

  return async (sendEndTransactionMessage = true) => {
    const queryArgs = confirmed?.callbackUrlQueryArgs ?? checkout?.merchant.callbackUrlQueryArgs ?? ''

    const logout = async (): Promise<void> => {
      if (authenticated && !persistent) {
        await dispatch(authLogout())
      }
    }

    const redirect = async (): Promise<void> => {
      const callbackUrl = getCallbackUrl()
      trackCloseCount()
      await clearSession()
      window.location.href = callbackUrl + (callbackUrl.includes('?') ? queryArgs : '?' + queryArgs)
    }

    const closePopup = async (): Promise<void> => {
      try {
        if (sendEndTransactionMessage) {
          await endTransaction(endTransactionMessage)
        }
      } catch (e) {
        reportError('No confirmation of end transaction', e)
      }
      trackCloseCount()
      await clearSession()
      if (!initial.isIframe) {
        // If we are using zoid then afterpay.js is responsible for closing the iframe
        window.close()
      }
    }

    const key = getCurrentUserContextFromClient(LDClient)?.key ?? null
    if (typeof LDClient !== 'undefined' && key !== null) {
      await LDClient.close()
    }

    await logout()

    stopDatadogSession()

    if (initial.isWindowed || initial.isIframe) {
      await closePopup()
    } else {
      await redirect()
    }
  }
}
